import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TelegramWebApp from '../telegram-init';

interface ErrorScreenProps {
  message?: string;
  onRetry?: () => void;
}

const ErrorScreen: React.FC<ErrorScreenProps> = ({ 
  message,
  onRetry 
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorState = location.state as { message?: string } | null;

  useEffect(() => {
    // Вибрация при появлении ошибки только при первом монтировании
    try {
      TelegramWebApp.HapticFeedback.notificationOccurred('error');
    } catch (e) {
      console.error('Haptic feedback error:', e);
    }
  }, []);

  // Определяем тип ошибки из сообщения
  const getErrorType = (msg: string = '') => {
    if (msg.includes('Docker')) return 'DOCKER';
    if (msg.includes('Network') || msg.includes('NETWORK')) return 'NETWORK';
    if (msg.includes('Service') || msg.includes('SERVICE')) return 'SERVICE';
    if (msg.includes('Database')) return 'DATABASE';
    return null;
  };

  // Получаем сообщение об ошибке из разных источников
  const urlMessage = searchParams.get('message');
  const errorMessage = message || urlMessage || errorState?.message;
  const errorType = getErrorType(errorMessage);
  const displayMessage = errorType 
    ? t(`ERROR_SCREEN.SPECIFIC_ERRORS.${errorType}`)
    : errorMessage || t('ERROR_SCREEN.DEFAULT_MESSAGE');

  const handleRetry = () => {
    try {
      TelegramWebApp.HapticFeedback.impactOccurred('medium');
    } catch (e) {
      console.error('Haptic feedback error:', e);
    }

    if (onRetry) {
      onRetry();
    } else {
      navigate(-1);
    }
  };

  const handleBack = () => {
    try {
      TelegramWebApp.HapticFeedback.impactOccurred('light');
    } catch (e) {
      console.error('Haptic feedback error:', e);
    }
    navigate(-1);
  };

  return (
    <div 
      className="fixed top-[60px] left-0 right-0 bottom-[80px] flex flex-col items-center justify-center bg-tg-bg overflow-hidden p-0 touch-none max-[640px]:p-4"
    >
      <button 
        onClick={handleBack} 
        className="absolute top-[3.25rem] left-3 p-2 rounded-lg bg-transparent border-none cursor-pointer transition-all duration-200 flex items-center justify-center text-tg-text opacity-70 active:opacity-50 active:-translate-x-0.5"
        aria-label={t('COMMON.BACK', 'Back')}
      >
        <ArrowLeft size={20} />
      </button>
      
      <div className="max-w-[20rem] w-full flex flex-col items-center justify-center text-center px-6 relative max-[640px]:px-4">
        {/* Градиентный фон */}
        <div 
          className="absolute inset-0 z-[1] pointer-events-none opacity-60"
          style={{
            background: 'radial-gradient(circle at 20% 20%, rgba(30, 58, 138, 0.15), transparent 50%), radial-gradient(circle at 80% 80%, rgba(30, 58, 138, 0.15), transparent 50%)'
          }}
        />
        
        <div 
          className="mb-6 relative z-[2] max-[640px]:mb-4"
          style={{
            animation: 'iconBounce 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both'
          }}
        >
          <AlertTriangle 
            className="w-[4.5rem] h-[4.5rem] text-[#FF0080] max-[640px]:w-16 max-[640px]:h-16"
            style={{
              filter: 'drop-shadow(0 4px 12px rgba(255, 0, 128, 0.2))'
            }}
          />
        </div>
        
        <h1 
          className="text-2xl font-bold mb-3 text-tg-text max-[640px]:text-xl"
          style={{
            animation: 'textAppear 0.5s ease-out 0.3s both'
          }}
        >
          {t('ERROR_SCREEN.TITLE')}
        </h1>
        
        <p 
          className="text-tg-hint mb-8 text-base leading-6 opacity-80 max-[640px]:text-[0.9375rem]"
          style={{
            animation: 'textAppear 0.5s ease-out 0.4s both'
          }}
        >
          {displayMessage}
        </p>

        <button
          onClick={handleRetry}
          className="w-full max-w-[280px] py-3.5 flex items-center justify-center gap-2 bg-gradient-pink text-white border-none rounded-[10px] font-semibold text-base cursor-pointer transition-all duration-200 relative z-[2] active:scale-[0.98] active:opacity-90 max-[640px]:py-3"
          style={{
            animation: 'buttonAppear 0.5s ease-out 0.5s both'
          }}
          aria-label={t('ERROR_SCREEN.RETRY_BUTTON')}
        >
          <RefreshCw className="w-5 h-5" />
          {t('ERROR_SCREEN.RETRY_BUTTON')}
        </button>
      </div>
    </div>
  );
};

export default ErrorScreen; 